import createClient, { type Middleware } from 'openapi-fetch';
import type { paths } from './schema';

/**
 * The generated schema paths already carry `/api/v1`, so the typed client mounts at the site
 * root. `API_PREFIX` is for the handful of calls made outside the typed client - file exports
 * and uploads - which are written without it.
 */
export const API_PREFIX = '/api/v1';

/** The organisation the user is currently working in, sent on every request. */
let activeOrganisation: string | null = null;

export function setActiveOrganisation(slugOrId: string | number | null) {
  activeOrganisation = slugOrId === null ? null : String(slugOrId);
}

export function getActiveOrganisation() {
  return activeOrganisation;
}

function readCookie(name: string): string | null {
  const match = document.cookie.match(new RegExp(`(?:^|; )${name}=([^;]*)`));
  return match ? decodeURIComponent(match[1]) : null;
}

/** Django's CSRF cookie name, set in `config/settings/base.py`. */
const CSRF_COOKIE = 'fly_csrftoken';
const UNSAFE_METHODS = new Set(['POST', 'PUT', 'PATCH', 'DELETE']);

export class ApiError extends Error {
  status: number;
  /** Field-keyed messages from DRF, ready to hand to a Mantine form. */
  fieldErrors: Record<string, string[]>;
  detail: string;

  constructor(status: number, body: unknown) {
    const { detail, fieldErrors } = parseErrorBody(status, body);
    super(detail);
    this.name = 'ApiError';
    this.status = status;
    this.detail = detail;
    this.fieldErrors = fieldErrors;
  }

  get isUnauthenticated() {
    return this.status === 401 || this.status === 403;
  }

  get isServerError() {
    return this.status >= 500;
  }
}

/** Cloudflare / nginx error pages must not be shown as the API's message. */
function isHtmlErrorBody(value: string) {
  const trimmed = value.trimStart();
  return trimmed.startsWith('<') || /<html[\s>]/i.test(trimmed.slice(0, 200));
}

function parseErrorBody(status: number, body: unknown) {
  const fieldErrors: Record<string, string[]> = {};
  let detail = `Request failed with status ${status}`;

  if (body && typeof body === 'object') {
    const record = body as Record<string, unknown>;
    if (typeof record.detail === 'string' && !isHtmlErrorBody(record.detail)) {
      detail = record.detail;
    }
    for (const [key, value] of Object.entries(record)) {
      if (key === 'detail') continue;
      const messages = Array.isArray(value) ? value.map(String) : [String(value)];
      if (messages.some(isHtmlErrorBody)) continue;
      fieldErrors[key] = messages;
    }
    if (detail.startsWith('Request failed')) {
      const first = Object.values(fieldErrors)[0];
      if (first?.length) detail = first[0];
    }
  } else if (typeof body === 'string' && body && !isHtmlErrorBody(body)) {
    detail = body;
  }

  return { detail, fieldErrors };
}

const organisationMiddleware: Middleware = {
  async onRequest({ request }) {
    if (activeOrganisation) {
      request.headers.set('X-Organisation', activeOrganisation);
    }
    if (UNSAFE_METHODS.has(request.method.toUpperCase())) {
      const token = readCookie(CSRF_COOKIE);
      if (token) request.headers.set('X-CSRFToken', token);
    }
    return request;
  },
  async onResponse({ response }) {
    return response;
  },
};

export const api = createClient<paths>({
  // Session cookies do the authentication, so every call has to carry them.
  credentials: 'same-origin',
});

api.use(organisationMiddleware);

/** Unwraps an openapi-fetch result, throwing ApiError so TanStack Query can handle failures. */
export function unwrap<T>(result: { data?: T; error?: unknown; response: Response }): T {
  if (result.error !== undefined || !result.response.ok) {
    throw new ApiError(result.response.status, result.error);
  }
  return result.data as T;
}

/**
 * Fetch for the calls the typed client cannot express: binary exports and multipart uploads.
 * `path` is relative to `API_PREFIX`, for example `/auth/csrf/`.
 */
export async function rawRequest(path: string, init: RequestInit = {}) {
  const headers = new Headers(init.headers);
  if (activeOrganisation) headers.set('X-Organisation', activeOrganisation);
  if (UNSAFE_METHODS.has((init.method ?? 'GET').toUpperCase())) {
    const token = readCookie(CSRF_COOKIE);
    if (token) headers.set('X-CSRFToken', token);
  }

  const response = await fetch(`${API_PREFIX}${path}`, {
    ...init,
    headers,
    credentials: 'same-origin',
  });

  if (!response.ok) {
    let body: unknown;
    try {
      body = await response.json();
    } catch {
      body = await response.text();
    }
    throw new ApiError(response.status, body);
  }
  return response;
}

/** Triggers a browser download for a PDF or XLSX export. */
export async function downloadFile(path: string, fallbackName: string) {
  const response = await rawRequest(path);
  const blob = await response.blob();
  const disposition = response.headers.get('Content-Disposition') ?? '';
  const match = disposition.match(/filename="?([^"]+)"?/);
  const link = document.createElement('a');
  link.href = URL.createObjectURL(blob);
  link.download = match?.[1] ?? fallbackName;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(link.href);
}

/** Called once at boot: Django only sets the CSRF cookie when something asks for it. */
export async function primeCsrf() {
  await rawRequest('/auth/csrf/');
}
